<template>
  <div class="cs-root">
    <!-- Topbar -->
    <div class="cs-topbar">
      <div class="cs-brand">
       
     <img src="@/assets/logo.png" alt="logo" style="height:50px; width:100px; object-fit:contain;">
      </div>
      <button class="cs-logout" @click="logout">
        <i class="fa fa-sign-out-alt"></i> Logout
      </button>
    </div>

    <!-- Hero Section -->
    <div class="cs-hero">
      <div class="cs-hero-content">
        <div class="cs-hero-left">
          <div class="cs-avatar-container">
            <div class="cs-avatar-ring"></div>
            <div class="cs-avatar">{{ initials }}</div>
          </div>
          <div class="cs-hero-text">
            <h1 class="cs-greeting">
              Welcome back, <span class="cs-name-highlight">{{ firstName }}</span>
            </h1>
            <p class="cs-sub">Select a workspace to continue your work</p>
          </div>
        </div>
        <div class="cs-hero-stats" v-if="!loading && companies.length > 0">
          <div class="cs-stat-item">
            <span class="cs-stat-value">{{ companies.length }}</span>
            <span class="cs-stat-label">Workspaces</span>
          </div>
          <div class="cs-stat-divider"></div>
          <div class="cs-stat-item">
            <span class="cs-stat-value">{{ activeCount }}</span>
            <span class="cs-stat-label">Active</span>
          </div>
          <div class="cs-stat-divider"></div>
          <div class="cs-stat-item">
            <span class="cs-stat-value">{{ inactiveCount }}</span>
            <span class="cs-stat-label">Inactive</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Body -->
    <div class="cs-body">
      <!-- Search -->
      <div class="cs-toolbar" v-if="!loading && companies.length > 0">
        <div class="cs-search-wrap">
          <i class="fa fa-search cs-search-icon"></i>
          <input
            v-model="query"
            class="cs-search"
            placeholder="Search workspaces..."
            type="text"
          />
          <button v-if="query" class="cs-search-clear" @click="query = ''">
            <i class="fa fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="cs-loading">
        <div class="cs-skeleton-grid">
          <div class="cs-skeleton" v-for="n in 6" :key="n"></div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredCompanies.length === 0 && companies.length === 0" class="cs-empty">
        <div class="cs-empty-icon-wrapper">
          <i class="fa fa-building cs-empty-icon"></i>
        </div>
        <h3 class="cs-empty-title">No workspaces available</h3>
        <p class="cs-empty-desc">You are not assigned to any company yet. Contact your administrator.</p>
      </div>

      <!-- No Results -->
      <div v-else-if="filteredCompanies.length === 0" class="cs-empty">
        <div class="cs-empty-icon-wrapper">
          <i class="fa fa-search cs-empty-icon"></i>
        </div>
        <h3 class="cs-empty-title">No results found</h3>
        <p class="cs-empty-desc">No workspaces match "{{ query }}"</p>
        <button class="cs-clear-btn" @click="query = ''">Clear search</button>
      </div>

      <!-- Company Grid -->
      <div v-else class="cs-grid">
        <button
          v-for="(company, index) in filteredCompanies"
          :key="company.external_id"
          :class="[
            'cs-card', 
            { 
              'cs-card-selecting': selectingId === company.external_id,
              'cs-card-inactive': company.status !== 'active'
            }
          ]"
          :style="{ '--card-delay': index * 50 + 'ms' }"
          :disabled="!!selectingId"
          @click="selectCompany(company)"
        >
          <div class="cs-card-top">
            <div class="cs-card-avatar" :style="{ background: getCompanyColor(company.name) + '15' }">
              <img v-if="company.logo" :src="company.logo" :alt="company.name" />
              <span v-else :style="{ color: getCompanyColor(company.name) }">
                {{ companyInitials(company.name) }}
              </span>
            </div>
            <span :class="['cs-status-badge', company.status === 'active' ? 'cs-status-active' : 'cs-status-inactive']">
              <span class="cs-status-dot"></span>
              {{ company.status === 'active' ? 'Active' : 'Inactive' }}
            </span>
          </div>

          <div class="cs-card-body">
            <h3 class="cs-card-name">{{ company.name }}</h3>
            <div class="cs-card-details">
              <div v-if="company.email" class="cs-card-detail">
                <i class="fa fa-envelope"></i>
                <span>{{ company.email }}</span>
              </div>
              <div v-if="company.phone" class="cs-card-detail">
                <i class="fa fa-phone"></i>
                <span>{{ company.phone }}</span>
              </div>
              <div v-if="company.address" class="cs-card-detail">
                <i class="fa fa-map-marker-alt"></i>
                <span>{{ company.address }}</span>
              </div>
            </div>
          </div>

          <div class="cs-card-footer">
            <div v-if="selectingId !== company.external_id" class="cs-card-action">
              <span>Enter Workspace</span>
              <i class="fa fa-arrow-right"></i>
            </div>
            <div v-else class="cs-card-action cs-card-loading">
              <i class="fa fa-spinner fa-spin"></i>
              <span>Loading...</span>
            </div>
          </div>

          <div v-if="selectingId === company.external_id" class="cs-card-overlay">
            <i class="fa fa-spinner fa-spin"></i>
          </div>
        </button>
      </div>
    </div>

    <!-- Footer -->
    <div class="cs-footer">
        <span>© 2026 FVN-Maintenance. All rights reserved.</span>
    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'

const COLORS = [
  '#4642c9', '#534AB7', '#0F6E56', '#854F0B',
  '#A32D2D', '#0D7377', '#1a6b3a', '#6b3a1a'
]

export default {
  name: 'CompanySelect',

  data() {
    return {
      companies: [],
      loading: false,
      selectingId: null,
      query: '',
    }
  },

  computed: {
    firstName() {
      return localStorage.getItem('user_fname') || 'User'
    },
    initials() {
      const f = localStorage.getItem('user_fname')?.[0] || ''
      const l = localStorage.getItem('user_lname')?.[0] || ''
      return (f + l).toUpperCase() || 'U'
    },
    activeCount() {
      return this.companies.filter(c => c.status === 'active').length
    },
    inactiveCount() {
      return this.companies.filter(c => c.status !== 'active').length
    },
    filteredCompanies() {
      if (!this.query.trim()) return this.companies
      const q = this.query.toLowerCase()
      return this.companies.filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.address?.toLowerCase().includes(q)
      )
    }
  },

  mounted() {
    if (localStorage.getItem('company_id')) {
      this.$router.replace('/dashboard')
      return
    }
    this.fetchCompanies()
  },

  methods: {
    async fetchCompanies() {
      this.loading = true
      const result = await form_api_submit({
        url: 'my-companies',
        method: 'GET',
        show_success_toast: false,
        show_error_toast: true,
      })
      if (result.success) {
        this.companies = result.data || []
        if (this.companies.length === 1) {
          await this.selectCompany(this.companies[0])
          return
        }
      }
      this.loading = false
    },

    async selectCompany(company) {
      this.selectingId = company.external_id
      const result = await form_api_submit({
        url: `company/select/${company.external_id}`,
        method: 'POST',
        show_success_toast: false,
        show_error_toast: true,
      })
      if (result.success) {
        localStorage.setItem('company_id', company.id)
        localStorage.setItem('company_name', company.name)
        localStorage.setItem('company_email', company.email || '')
        // localStorage.setItem('company_logo', company.logo || '')
        this.$router.push('/dashboard')
      } else {
        this.selectingId = null
      }
    },

    companyInitials(name) {
      if (!name) return '?'
      const words = name.trim().split(/\s+/)
      return ((words[0]?.[0] || '') + (words[1]?.[0] || '')).toUpperCase()
    },

    getCompanyColor(name) {
      if (!name) return COLORS[0]
      let hash = 0
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
      }
      return COLORS[Math.abs(hash) % COLORS.length]
    },
async logout() {
    this.loggingOut = true

    // Call backend logout to invalidate token
    await form_api_submit({
      url:                'logout',
      method:             'POST',
      show_success_toast: false,
      show_error_toast:   false,
    })

    // Clear all localStorage regardless of API result
    localStorage.clear()

    this.loggingOut       = false
    this.showSettingsMenu = false

    this.$router.push('/auth/login')
  },
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.cs-root {
  min-height: 100vh;
  background: var(--pp-bg-page);
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  display: flex;
  flex-direction: column;
}

/* ── Topbar ───────────────────────────────────────────────── */
.cs-topbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 2rem;
  background: var(--pp-bg-card);
  border-bottom: 1px solid var(--pp-border);
  position: sticky;
  top: 0;
  z-index: 10;
}

.cs-brand {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.cs-brand-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--pp-accent);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.cs-brand-name {
  font-size: 1rem;
  font-weight: 700;
  color: var(--pp-text-primary);
}

.cs-logout {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  border: 1px solid var(--pp-border);
  border-radius: var(--pp-radius-pill);
  background: transparent;
  color: var(--pp-text-secondary);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.cs-logout:hover {
  background: var(--pp-red-bg);
  border-color: var(--pp-red-border);
  color: var(--pp-red-text);
}

/* ── Hero Section ────────────────────────────────────────── */
.cs-hero {
  background: linear-gradient(135deg, var(--pp-accent) 0%, #3a37a8 100%);
  padding: 2.5rem 2rem;
  position: relative;
  overflow: hidden;
}

.cs-hero::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
}

.cs-hero-content {
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  z-index: 1;
}

.cs-hero-left {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.cs-avatar-container {
  position: relative;
  flex-shrink: 0;
}

.cs-avatar-ring {
  position: absolute;
  inset: -4px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.3);
  animation: pulse-ring 2s ease-out infinite;
}

@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 0.6;
  }
  100% {
    transform: scale(1.3);
    opacity: 0;
  }
}

.cs-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(4px);
  border: 2px solid rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  letter-spacing: 1px;
}

.cs-greeting {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  line-height: 1.2;
}

.cs-name-highlight {
  color: var(--pp-blue-bg);
}

.cs-sub {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 0.25rem;
}

.cs-hero-stats {
  display: flex;
  align-items: center;
  gap: 0;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: var(--pp-radius-card);
  padding: 0.75rem 1.5rem;
}

.cs-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 1.25rem;
}

.cs-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  line-height: 1;
}

.cs-stat-label {
  font-size: 0.65rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 0.25rem;
}

.cs-stat-divider {
  width: 1px;
  height: 32px;
  background: rgba(255, 255, 255, 0.2);
}

/* ── Body ────────────────────────────────────────────────── */
.cs-body {
  flex: 1;
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  padding: 2rem 1.5rem 3rem;
}

/* ── Toolbar ─────────────────────────────────────────────── */
.cs-toolbar {
  margin-bottom: 1.5rem;
}

.cs-search-wrap {
  position: relative;
  max-width: 400px;
}

.cs-search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--pp-text-muted);
  font-size: 0.85rem;
}

.cs-search {
  width: 100%;
  padding: 0.7rem 1rem 0.7rem 2.75rem;
  border: 1px solid var(--pp-border);
  border-radius: var(--pp-radius-field);
  background: var(--pp-bg-card);
  font-size: 0.9rem;
  color: var(--pp-text-primary);
  transition: all 0.2s;
}

.cs-search:focus {
  outline: none;
  border-color: var(--pp-accent);
  box-shadow: 0 0 0 3px var(--pp-accent-light);
}

.cs-search-clear {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--pp-text-muted);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 50%;
  transition: all 0.2s;
}

.cs-search-clear:hover {
  background: var(--pp-bg-field);
  color: var(--pp-text-secondary);
}

/* ── Loading Skeleton ────────────────────────────────────── */
.cs-skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.25rem;
}

.cs-skeleton {
  height: 220px;
  border-radius: var(--pp-radius-card);
  background: linear-gradient(90deg, var(--pp-border) 25%, var(--pp-border-soft) 50%, var(--pp-border) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ── Empty States ────────────────────────────────────────── */
.cs-empty {
  text-align: center;
  padding: 4rem 2rem;
}

.cs-empty-icon-wrapper {
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  border-radius: 50%;
  background: var(--pp-accent-light);
  display: flex;
  align-items: center;
  justify-content: center;
}

.cs-empty-icon {
  font-size: 2rem;
  color: var(--pp-accent);
}

.cs-empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--pp-text-primary);
  margin-bottom: 0.5rem;
}

.cs-empty-desc {
  font-size: 0.9rem;
  color: var(--pp-text-secondary);
}

.cs-clear-btn {
  margin-top: 1.25rem;
  padding: 0.6rem 1.5rem;
  background: var(--pp-accent);
  color: #fff;
  border: none;
  border-radius: var(--pp-radius-pill);
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.cs-clear-btn:hover {
  background: var(--pp-accent-text);
}

/* ── Grid ────────────────────────────────────────────────── */
.cs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(270px, 1fr));
  gap: 1.25rem;
}

/* ── Card ────────────────────────────────────────────────── */
.cs-card {
  position: relative;
  background: var(--pp-bg-card);
  border: 1px solid var(--pp-border);
  border-radius: var(--pp-radius-card);
  padding: 1.5rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  animation: card-fade-in 0.4s ease forwards;
  animation-delay: var(--card-delay);
  opacity: 0;
}

@keyframes card-fade-in {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.cs-card:hover:not(:disabled) {
  transform: translateY(-4px);
  box-shadow: var(--pp-shadow-hover);
  border-color: var(--pp-accent-border);
}

.cs-card:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.cs-card-inactive {
  opacity: 0.7;
}

.cs-card-selecting {
  opacity: 0.6;
  pointer-events: none;
}

.cs-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.cs-card-avatar {
  width: 56px;
  height: 56px;
  border-radius: var(--pp-radius-chip);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 700;
  overflow: hidden;
}

.cs-card-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cs-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.25rem 0.7rem;
  border-radius: var(--pp-radius-pill);
  text-transform: capitalize;
}

.cs-status-active {
  background: var(--pp-green-bg);
  color: var(--pp-green-text);
}

.cs-status-inactive {
  background: var(--pp-red-bg);
  color: var(--pp-red-text);
}

.cs-status-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
}

.cs-card-body {
  margin-bottom: 1.25rem;
}

.cs-card-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--pp-text-primary);
  margin-bottom: 0.5rem;
}

.cs-card-details {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.cs-card-detail {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: var(--pp-text-secondary);
}

.cs-card-detail i {
  width: 14px;
  font-size: 0.7rem;
  color: var(--pp-text-muted);
}

.cs-card-detail span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cs-card-footer {
  padding-top: 1rem;
  border-top: 1px solid var(--pp-border-soft);
}

.cs-card-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--pp-accent);
  transition: all 0.2s;
}

.cs-card:hover .cs-card-action:not(.cs-card-loading) {
  gap: 0.5rem;
}

.cs-card-action i {
  transition: transform 0.2s;
}

.cs-card:hover .cs-card-action:not(.cs-card-loading) i {
  transform: translateX(4px);
}

.cs-card-loading {
  color: var(--pp-text-secondary);
}

.cs-card-loading i {
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.cs-card-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(2px);
  border-radius: var(--pp-radius-card);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: var(--pp-accent);
}

/* ── Footer ───────────────────────────────────────────────── */
.cs-footer {
  text-align: center;
  padding: 1.5rem;
  border-top: 1px solid var(--pp-border);
  font-size: 0.8rem;
  color: var(--pp-text-muted);
}

/* ── Responsive ───────────────────────────────────────────── */
@media (max-width: 768px) {
  .cs-hero {
    padding: 1.5rem 1.25rem;
  }

  .cs-hero-content {
    flex-direction: column;
    align-items: stretch;
    gap: 1.25rem;
  }

  .cs-hero-left {
    gap: 1rem;
  }

  .cs-avatar {
    width: 52px;
    height: 52px;
    font-size: 1.25rem;
  }

  .cs-greeting {
    font-size: 1.2rem;
  }

  .cs-hero-stats {
    justify-content: space-around;
    padding: 0.5rem;
  }

  .cs-stat-item {
    padding: 0 0.75rem;
  }

  .cs-stat-value {
    font-size: 1.25rem;
  }

  .cs-grid {
    grid-template-columns: 1fr;
  }

  .cs-body {
    padding: 1.25rem 1rem 2rem;
  }

  .cs-search-wrap {
    max-width: 100%;
  }

  .cs-topbar {
    padding: 0.5rem 1rem;
  }

  .cs-brand-name {
    font-size: 0.9rem;
  }

  .cs-logout {
    padding: 0.4rem 1rem;
    font-size: 0.8rem;
  }
}

@media (max-width: 480px) {
  .cs-hero-stats {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .cs-stat-divider {
    display: none;
  }

  .cs-stat-item {
    flex: 1;
    min-width: 60px;
  }
}
</style>